import { FormControl, FormLabel, InputAdornment, List, ListItem, TextField, Typography } from '@mui/material'
import { createElement } from 'react'
import { ElementFactory, Question, Serializer } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { parseSurveyText } from '../components/parseSurveyText'

const CUSTOM_TYPE = 'surveynumber'

export class SurveyNumberModel extends Question {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
    this.createItemValues('bulletedDescription')
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get min (): number | undefined {
    return this.getPropertyValue('min')
  }

  set min (val: number | undefined) {
    this.setPropertyValue('min', val)
  }

  get max (): number | undefined {
    return this.getPropertyValue('max')
  }

  set max (val: number | undefined) {
    this.setPropertyValue('max', val)
  }

  get placeholder (): string {
    return this.getPropertyValue('placeholder') ?? ''
  }

  set placeholder (val: string) {
    this.setPropertyValue('placeholder', val)
  }

  get startAdornment (): string | undefined {
    return this.getPropertyValue('startAdornment')
  }

  set startAdornment (val: string | undefined) {
    this.setPropertyValue('startAdornment', val)
  }

  get bulletedDescription (): Array<{ value: number, text: string }> {
    return this.getPropertyValue('bulletedDescription')
  }

  set bulletedDescription (val) {
    this.setPropertyValue('bulletedDescription', val)
  }

  isEmpty (): boolean {
    return this.value == null || this.value === ''
  }
}

export function registerNumberQuestion (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyNumberModel(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [{
      name: 'min',
      type: 'number',
      category: 'general'
    }, {
      name: 'max',
      type: 'number',
      category: 'general'
    }, {
      name: 'placeholder',
      type: 'string',
      category: 'general'
    }, {
      name: 'startAdornment',
      type: 'string',
      category: 'general'
    }, {
      name: 'bulletedDescription',
      type: 'itemvalues',
      category: 'general'
    }],
    function () {
      return new SurveyNumberModel('')
    },
    'question'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionNumber, props)
  })
}

export class SurveyQuestionNumber extends SurveyQuestionElementBase {
  get question (): SurveyNumberModel {
    return this.questionBase as SurveyNumberModel
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value
    if (raw === '') {
      this.question.value = undefined
    } else {
      const num = Number(raw)
      if (!isNaN(num)) {
        this.question.value = num
      }
    }
  }

  renderElement (): JSX.Element {
    const { min = 0, max } = this.question

    return (
      <FormControl>
        <FormLabel className='surveyQuestionTitle'>
          {this.question.fullTitle}
          {!this.question.isRequired && (
            <Typography component="span" sx={{ fontStyle: 'italic', ml: 1 }}>
              optional
            </Typography>
          )}
        </FormLabel>
        {this.question.description != null && this.question.description !== '' && (
          <Typography variant="body2" className='surveyQuestionDescription' sx={{ width: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 1, mb: this.question.bulletedDescription?.length > 0 ? 0 : 2 }}>
            {parseSurveyText(this.question.description)}
          </Typography>
        )}
        {(this.question.bulletedDescription != null && this.question.bulletedDescription.length > 0) && (
          <List sx={{ listStyleType: 'disc', pl: 4, mt: 0, mb: 2 }}>
            {this.question.bulletedDescription.map((item: { value: number, text: string }, index) => (
              <ListItem key={index} sx={{ display: 'list-item', p: 0 }}>
                <Typography variant="body2">{parseSurveyText(item.text)}</Typography>
              </ListItem>
            ))}
          </List>
        )}
        <TextField
          type="number"
          className='surveyNumberField surveyQuestionField'
          disabled={this.question.isReadOnly}
          value={this.question.value ?? ''}
          onChange={this.handleChange}
          placeholder={this.question.placeholder}
          InputProps={this.question.startAdornment != null
            ? { startAdornment: <InputAdornment position="start">{this.question.startAdornment}</InputAdornment> }
            : undefined}
          inputProps={{
            min,
            max,
            'data-testid': this.question.name
          }}
          sx={{
            maxWidth: '300px',
            '& .MuiOutlinedInput-root': {
              py: 0
            },
            '& input[type=number]': {
              MozAppearance: 'textfield'
            }
          }}
        />
      </FormControl>
    )
  }
}
